/**
 * @author: Pankaj Kulshreshtha | Cheers Interactive
 * @date : 06/Aug/2021
 * File Description:  Add / Edit Colaboration Modal
 */
import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import { setSideNavForcedActiveLink } from "../../../../../middleware/actions/sideNavAction";
import { setBreadCrumb } from "../../../../../middleware/actions/breadCrumbAction";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ckEditorMinimalConfig } from "../../../../../config/ckEditorConfig";
import { getProductVariant, accessRightActionCheck } from "../../../../../utilities";
import { actionSuccess, actionError, showLoader, hideLoader } from "../../../../../middleware/actions/utilityAction";
import { collaborationValidationSchema } from "../../validation/companyValidationSchema";
import {
  fetchSingleCollaboration,
  saveCollaboration,
  updateCollaboration,
  fetchSingleCompany,
  saveFunding,
  deleteManycollaborationTrend,
} from "../../../../../middleware/services/companyApi";
import { fetchTaxonomyTechnology, fetchTaxonomyTrend, fetchTaxonomyIndustry } from "../../../../../middleware/services/cmsApi";
import { Dropdown, TextField, TextEditor, DatePicker } from "../../../../components/CustomFormElements/syncFusionFormFields";
import { COLLABORATION_TYPE, PRODUCT_VARIANT, COMMON_ACCESS_ACTION } from "../../../../../constants";
import fundingTypeOptions, { COLLABORATION_TYPES } from "../../constants";
import { Modal } from "react-bootstrap";
import PropTypes from "prop-types";
import { removeHtmlTags } from "utils";

const initialState = {
  pVariant: getProductVariant(),
  collaborationName: "",
  collaborationType: "",
  collaborationDealValue: "",
  collaborationDate: "",
  collaborationDescription: "",
  collaborationTechnologyPurpose: "",
  collaborationPartnerSource: [],
  collaborationPartnerTarget: [],
  collaborationSourceUrl: "",
  collaborationCountry: "",
  collaborationRegion: "",
  collaborationTechnologyTags: [],
  collaborationIndustryTags: [],
  collaborationTrendIndustryTags: [],
  collaborationIndustryTrendsTags: [],
  undisclosed: "Undisclosed",
  fundingTypeRound: "",
};

const AddCollaboration = (props) => {
  const dispatch = useDispatch();
  const companyId = props?.computedMatch?.params?.companyId;
  const collaborationId = props?.computedMatch?.params?.collaborationId || "";
  const [companyData, setCompanyData] = useState(null);
  const [collaborationData, setCollaborationData] = useState(null);
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [contentLength, setContentLength] = useState(0);
  const [saveTreeDataAction, setSaveTreeDataAction] = useState(false);
  const [displayUpdateMessageFlag, setDisplayUpdateMessageFlag] = useState(true);
  const [showModalAdd, setShowModalAdd] = useState(false);

  const sourceRoles = ["Acquirer", "Adopter", "Investor", "Licenser", "Source"];
  const targetRoles = ["Acquiree", "Adoptee", "Investee", "Licensee", "Target"];

  const history = useHistory();

  const split_url = props.location?.pathname.split("/");

  const backPath = collaborationId
    ? `${split_url.slice(0, -3).join("/")}/edit#collaboration`
    : `${split_url.slice(0, -2).join("/")}/edit#collaboration`;

  const productVariant = getProductVariant();
  const { accessRights: aR } = props;
  const [interfaceActionAccess, setInterfaceActionAccess] = useState([]);
  /* Interface functionality wise access  */
  useEffect(() => {
    let interfaceName = "Add New Collaboration";
    let actionAccess = accessRightActionCheck(aR.moduleName, interfaceName);
    setInterfaceActionAccess(actionAccess);
  }, []);

  /* Common access */
  const accessActionSave = interfaceActionAccess.includes(COMMON_ACCESS_ACTION.SAVE);

  const formik = useFormik({
    initialValues: { ...initialState },
    validationSchema: Yup.object().shape(collaborationValidationSchema),
  });
  const breadCrumbLinks = [
    { linkUrl: "/company", linkName: aR.moduleName, linkActive: false },
    {
      linkUrl: companyId ? `/company/${companyId}/edit#collaboration` : "/company/edit#collaboration",
      linkName: companyData?.companyName || "",
      linkActive: false,
    },
    { linkUrl: `/company/${companyId}/edit#collaboration`, linkName: "Collaboration", linkActive: false },
    {
      linkUrl: companyId ? `/company/${companyId}/edit#collaboration` : "/company/edit#collaboration",
      linkName: collaborationData?.collaborationName || "Add",
      linkActive: true,
    },
  ];

  const getCompany = () => {
    let params = {};
    let fields = ["companyName"];
    params.fields = fields;
    fetchSingleCompany(companyId, params).then((res) => {
      let data = res.data.data || {};
      setCompanyData(data);
    });
  };

  const saveCollaborationDetails = async () => {
    dispatch(showLoader());
    formik.validateForm().then(async (res) => {
      if (Object.keys(res).length) {
        const touched = {};
        Object.keys(res).map((field) => {
          touched[field] = true;
          return touched;
        });
        formik.setFormikState({ ...formik, touched: touched, errors: res });
        dispatch(hideLoader());
      } else {
        setBtnDisabled(true);
        let payload = {};
        const {
          collaborationName,
          collaborationDescription,
          collaborationType,
          collaborationDealValue,
          collaborationDate,
          collaborationSourceUrl,
          collaborationTechnologyPurpose,
          fundingTypeRound,
        } = formik.values;

        let collaborationDate1 = new Date(collaborationDate);
        collaborationDate1.setHours(collaborationDate1.getHours() + 1);
        payload.collaborationName = collaborationName;
        payload.collaborationDescription = collaborationDescription;
        payload.collaborationType = collaborationType;
        payload.collaborationDealValue = collaborationDealValue;
        payload.collaborationDealCurrency = "USD";
        payload.collaborationDate = collaborationDate ? collaborationDate1 : "";

        if (collaborationType === COLLABORATION_TYPES.FUNDING_INVESTMENT) {
          payload.collaborationSubType = fundingTypeRound;
        }

        if (collaborationSourceUrl) {
          payload.collaborationSourceUrl = collaborationSourceUrl;
        }

        if (collaborationTechnologyPurpose) {
          payload.collaborationTechnologyPurpose = collaborationTechnologyPurpose;
        }

        if (collaborationId) {
          updateCollaboration(collaborationId, payload)
            .then((res) => {
              dispatch(actionSuccess("Collaboration details updated successfully"));
              setDisplayUpdateMessageFlag(true);
              setBtnDisabled(false);
              dispatch(hideLoader());
              history.push(backPath);
            })
            .catch((err) => {
              dispatch(actionError(err?.data?.message || "Something went wrong"));
              setBtnDisabled(false);
              dispatch(hideLoader());
            });
        } else {
          payload.companyId = companyId;
          payload.companyName = companyData?.companyName;
          payload.collaborationStatus = "Active";

          saveCollaboration(payload)
            .then((res) => {
              if (res) {
                dispatch(actionSuccess("Collaboration details saved successfully"));
                setDisplayUpdateMessageFlag(false);
                setBtnDisabled(false);
                history.push({
                  pathname: `/company/${companyId}/collaboration/${res.data.data.id}/edit`,
                  state: { collaborationId: res.data.data.id },
                });
                setSaveTreeDataAction("save");
                setCollaborationData(payload);
                dispatch(hideLoader());
                history.push(backPath);
              } else {
                dispatch(actionError("something went wrong"));
                setBtnDisabled(false);
                dispatch(hideLoader());
              }
            })
            .catch((err) => {
              dispatch(actionError(err?.data?.message || "something went wrong"));
              setBtnDisabled(false);
              dispatch(hideLoader());
            });
        }

        if (collaborationType === COLLABORATION_TYPES.FUNDING_INVESTMENT) {
          let payload = {};
          payload.fundingName = collaborationName;
          payload.fundingDate = collaborationDate1;
          payload.fundingTypeRound = fundingTypeRound;
          payload.fundingAmount = collaborationDealValue;
          payload.fundingComment = collaborationDescription;

          saveFunding(payload)
            .then((response) => {})
            .catch((err) => {});
        }
        dispatch(hideLoader());
      }
    });
  };

  /**
   * @description fetch single collaboration detail
   * @param {String} collaborationId
   */
  const fetchCollaborationDetail = async () => {
    dispatch(showLoader());
    collaborationId &&
      (await fetchSingleCollaboration(collaborationId)
        .then((res) => {
          const collaborationDetail = { ...res.data.data };
          setCollaborationData(collaborationDetail);

          collaborationDetail &&
            formik.setValues({
              collaborationName: collaborationDetail.collaborationName || "",
              collaborationType: collaborationDetail.collaborationType || "",
              collaborationDealValue: collaborationDetail.collaborationDealValue || "",
              collaborationDate: collaborationDetail.collaborationDate || "",
              collaborationDescription: collaborationDetail.collaborationDescription || "",
              collaborationStatusComment: collaborationDetail.collaborationStatusComment || "",
              collaborationTechnologyPurpose: collaborationDetail.collaborationTechnologyPurpose || "",
              collaborationSourceUrl: collaborationDetail.collaborationSourceUrl || "",
              undisclosed: "Undisclosed",
              fundingTypeRound: collaborationDetail.collaborationSubType || "",
            });

          dispatch(hideLoader());
        })
        .catch((err) => {
          console.log("err", err);
          let errMsg = err?.data?.message ? err?.data?.message : "Fetch Collaboration Details Failed";
          dispatch(actionError(errMsg));
        }));
  };

  useEffect(() => {
    setContentLength(
      removeHtmlTags(formik.values.collaborationDescription) === " "
        ? removeHtmlTags(formik.values.collaborationDescription)?.trim().length
        : removeHtmlTags(formik.values.collaborationDescription)?.length
    );
  }, [formik.values.collaborationDescription]);

  useEffect(() => {
    companyId && getCompany();
  }, [companyId]);

  useEffect(() => {
    collaborationId && !collaborationData && fetchCollaborationDetail();
  }, []);

  useEffect(() => {
    dispatch(setBreadCrumb(breadCrumbLinks));
    dispatch(setSideNavForcedActiveLink("/company"));
  }, [companyData, collaborationData, dispatch]);

  return (
    <>
      <div className="add-collaboration" style={{ height: "100%", background: "#ffffff" }}>
        <div className="collaboration-form m-4">
          <div className="collaboration-form-title-box">
            <div className="collaboration-form-title">{collaborationId ? "Update" : "Add New"} Collaboration</div>
          </div>
          <div className="collaboration-form-box">
            <div className="row mt-4">
              <div className="col-md-4">
                <input type="hidden" name="pVariant" id="pVariant" value={productVariant} />
                <TextField formik={formik} type="text" id="collaborationName" name="collaborationName" placeholder="Collaboration Name *" />
              </div>
              {formik.values.collaborationType !== COLLABORATION_TYPES.FUNDING_INVESTMENT && (
                <div className="col-md-4">
                  <Dropdown
                    formik={formik}
                    id="collaborationType"
                    name="collaborationType"
                    placeholder="Collaboration Type *"
                    dataSource={COLLABORATION_TYPE}
                    allowFiltering={true}
                    filtering={() => {
                      return false;
                    }}
                    fields={{ text: "label", value: "value" }}
                    value={formik.values.collaborationType}
                  />
                </div>
              )}
              {formik.values.collaborationType === COLLABORATION_TYPES.FUNDING_INVESTMENT && (
                <div className="col-md-2">
                  <Dropdown
                    formik={formik}
                    id="collaborationType"
                    name="collaborationType"
                    placeholder="Collaboration Type *"
                    dataSource={COLLABORATION_TYPE}
                    allowFiltering={true}
                    filtering={() => {
                      return false;
                    }}
                    fields={{ text: "label", value: "value" }}
                    value={formik.values.collaborationType}
                  />
                </div>
              )}
              {formik.values.collaborationType === COLLABORATION_TYPES.FUNDING_INVESTMENT && (
                <div className="col-md-2">
                  <Dropdown
                    formik={formik}
                    id="fundingTypeRound"
                    name="fundingTypeRound"
                    placeholder="Funding Type *"
                    dataSource={fundingTypeOptions}
                    fields={{ text: "label", value: "value" }}
                  />
                </div>
              )}
              <div className="col-md-2" data-tip="Enter NA if Amount not available">
                <TextField
                  formik={formik}
                  format="c0"
                  min={0}
                  id="collaborationDealValue"
                  name="collaborationDealValue"
                  placeholder="Deal Value (In USD Millions) *"
                />
              </div>
              <div className="col-md-2">
                <DatePicker
                  formik={formik}
                  properties={{
                    placeholder: "Deal Date",
                    id: `collaborationDate`,
                    name: `collaborationDate`,
                    format: "dd MMM yyyy",
                    floatLabelType: "Auto",
                    allowEdit: false,
                    openOnFocus: true,
                    max: new Date(),
                  }}
                />
              </div>

              <div className="col-md-4">
                <TextField formik={formik} type="url" id="collaborationSourceUrl" placeholder="Source URL" name="collaborationSourceUrl" />
              </div>
            </div>
            {productVariant === PRODUCT_VARIANT.INSIDER ? (
              <div className="row" style={{ paddingTop: "1em" }}>
                <div className="col-md-6">
                  <label style={{ top: "20px", position: "relative" }}>Collaboration Description *</label>
                </div>
                <div className="col-md-6">
                  <label style={{ top: "20px", position: "relative" }}>Technology Purpose</label>
                </div>
              </div>
            ) : (
              <div className="row">
                <div className="col-md-6">
                  <label style={{ top: "20px", position: "relative" }}>Collaboration Description *</label>
                </div>
                <div className="col-md-6">
                  <label style={{ top: "20px", position: "relative" }}>Technology Purpose</label>
                </div>
              </div>
            )}
            {productVariant === PRODUCT_VARIANT.INSIDER ? (
              <div className="row">
                <div className="col-md-6">
                  <TextEditor
                    maxLength={1000}
                    formik={formik}
                    properties={{
                      id: "collaborationDescription",
                      name: "collaborationDescription",
                      activeClass: "collaborationDescription",
                      config: { ...ckEditorMinimalConfig, editorplaceholder: "Collaboration Description", height: "200px" },
                    }}
                  />
                </div>

                <div className="col-md-6">
                  <TextEditor
                    maxLength={1000}
                    formik={formik}
                    properties={{
                      id: "collaborationTechnologyPurpose",
                      name: "collaborationTechnologyPurpose",
                      activeClass: "collaborationTechnologyPurpose",
                      config: { ...ckEditorMinimalConfig, editorplaceholder: "Technology Purpose", height: "200px" },
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="row">
                <div className="col-md-6">
                  <TextEditor
                    formik={formik}
                    properties={{
                      id: "collaborationDescription",
                      name: "collaborationDescription",
                      activeClass: "collaborationDescription",
                      config: { ...ckEditorMinimalConfig, editorplaceholder: "Collaboration Description", height: "200px" },
                    }}
                  />
                </div>

                <div className="col-md-6">
                  <TextEditor
                    formik={formik}
                    properties={{
                      id: "collaborationTechnologyPurpose",
                      name: "collaborationTechnologyPurpose",
                      activeClass: "collaborationTechnologyPurpose",
                      config: { ...ckEditorMinimalConfig, editorplaceholder: "Technology Purpose", height: "200px" },
                    }}
                  />
                </div>
              </div>
            )}
          </div>
          <div className="collaboration-form-submit-box">
            <div className="container-fluid nopadding">
              <div className="row">
                <div className="float-right col-md-12 text-right">
                  <Link to={`/company/${companyId}/edit#collaboration` || history.location.pathname}>
                    <button type="button" className="btn btn-outline-dark company-button mr-3">
                      Cancel
                    </button>
                  </Link>
                  {accessActionSave ? (
                    <button
                      type="button"
                      disabled={btnDisabled ? "disabled" : ""}
                      className="btn btn-primary company-button"
                      onClick={() => {
                        saveCollaborationDetails();
                      }}
                    >
                      {collaborationId ? "Update" : "Save"}
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal show={showModalAdd} backdrop="static" className="modal fade lg" size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
        <Modal.Body>
          <div className="row">
            <div className="col-md-12 mb-3" style={{ marginLeft: "24px !important" }}>
              In the Investee field, only one company can be listed, and Undisclosed information is not allowed.
            </div>
          </div>
        </Modal.Body>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline-dark cy-btn" onClick={() => setShowModalAdd(!showModalAdd)}>
            Ok
          </button>
        </div>
      </Modal>
    </>
  );
};
AddCollaboration.propTypes = {
  accessRights: PropTypes.arrayOf(PropTypes.string),
  loadOtherTagInfo: PropTypes.func,
  computedMatch: PropTypes.string,
};
export default AddCollaboration;
